/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useApi } from 'shared_components/context';
import { DiscographyId, useDiscographyIds } from '../useDiscographyIds';
import { LoaderCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useClientId } from 'shared_components/context/client';
import { usePrimaryDiscographyRun } from '../usePrimaryDiscographyRun';

function CachedInfinity() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 16c5 0 7-8 12-8a4 4 0 0 1 0 8c-5 0-7-8-12-8a4 4 0 1 0 0 8" />
    </svg>
  );
}

function PermanentStatusCheck({ artist }: { artist: DiscographyId }) {
  const { getCollectedDiscographyIds } = useDiscographyIds();
  const [updating, setUpdating] = useState(false);
  const { clientId } = useClientId();
  const { adminApi: api } = useApi();
  const { actionsDisabled } = usePrimaryDiscographyRun();

  // The spinner outlives the request, so the component can now be unmounted
  // (row re-sorted away, modal closed) while a state update is still pending.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleOnTogglePermanentStatus = useCallback(async () => {
    if (!clientId || actionsDisabled || !artist.linked_alias_id) return;
    // The spinner is up for longer now, which is more room for a second click
    // — and two writes racing decide the final status by whichever lands last.
    if (updating) return;

    setUpdating(true);
    try {
      await api.updateBandArtistBand({
        userId: clientId,
        bandId: artist.id,
        artistBandUpdate: {
          linkedAliasId: artist.linked_alias_id,
          isPermanentMember: !Boolean(artist.is_permanent_member),
        },
      });
    } catch (err) {
      console.error(err);
    }

    // Awaited, not fired and forgotten. `artist` is provider state that this
    // read is what refreshes, so stopping the spinner when the *write* landed
    // left the infinity sign settled in its old colour until the read caught
    // up — the click looked ignored, then undone by something else.
    try {
      await getCollectedDiscographyIds(clientId);
    } finally {
      if (mounted.current) setUpdating(false);
    }
  }, [
    api,
    artist,
    actionsDisabled,
    clientId,
    updating,
    getCollectedDiscographyIds,
  ]);

  return (
    <div
      className={[
        `tw-inline-flex tw-w-6 tw-items-center tw-pl-2 tw-text-lg tw-rounded tw-select-none tw-h-5 tw-justify-center`,
        artist.is_permanent_member
          ? 'tw-text-westburyGreen'
          : 'tw-text-westburyRed',
        !actionsDisabled ? 'tw-cursor-pointer' : '',
      ].join(' ')}
      onClick={handleOnTogglePermanentStatus}
      title={
        artist.is_permanent_member ? 'Permanent member' : 'Non-permanent member'
      }
    >
      {updating ? (
        <LoaderCircle size={12} className="tw-animate-spin tw-text-white" />
      ) : (
        <CachedInfinity />
      )}
    </div>
  );
}

export default PermanentStatusCheck;
